import React from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { Bell, Compass, Home, Plus, User } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLang } from "@/lib/LanguageContext";
import { useAuth } from "@/lib/AuthContext";
import HelpButton from "@/components/ui/HelpButton";

export default function MobileLayout() {
  const { t } = useLang();
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const path = location.pathname;

  const hidePersistentChrome =
    /^\/create$/.test(path) ||
    /^\/event\/[^/]+(?:\/(organizer|payment|gift|qr|live))?$/.test(path);

  const connectedShellPaths = ["/dashboard", "/explore", "/profile", "/notifications", "/support"];
  const showBottomNav = isAuthenticated && connectedShellPaths.includes(path) && !hidePersistentChrome;
  const showHelpButton =
    showBottomNav &&
    ["/dashboard", "/profile", "/notifications"].includes(path);

  const navItems = [
    { path: "/dashboard", icon: Home, labelKey: "nav_home" },
    { path: "/explore", icon: Compass, labelKey: "nav_explore" },
    { path: "/create", icon: Plus, labelKey: "nav_create", special: true },
    { path: "/notifications", icon: Bell, labelKey: "nav_notifications" },
    { path: "/profile", icon: User, labelKey: "nav_profile" },
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="flex-1 overflow-auto">
        <Outlet />
      </div>

      {showHelpButton && (
        <HelpButton bottomOffset="calc(5.75rem + env(safe-area-inset-bottom, 0px))" />
      )}

      {showBottomNav && (
        <nav
          aria-label={t("nav_primary")}
          className="sticky bottom-0 z-30 border-t border-border bg-card/95 backdrop-blur-xl mobile-safe-bottom"
        >
          <div className="grid max-w-lg grid-cols-5 items-end gap-1 px-2 pb-1 pt-2 mx-auto">
            {navItems.map(({ path, icon: Icon, labelKey, special }) =>
              special ? (
                <NavLink
                  key={`${labelKey}-${path}`}
                  to={path}
                  aria-label={t("topbar_create")}
                  className="flex min-w-0 flex-col items-center gap-1 text-primary"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-brand-sm transition-transform active:scale-95">
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="max-w-full truncate text-[11px] font-semibold leading-tight">{t(labelKey)}</span>
                </NavLink>
              ) : (
                <NavLink key={`${labelKey}-${path}`} to={path}
                  className={({ isActive }) =>
                    cn(
                      "flex min-h-12 min-w-0 flex-col items-center justify-center gap-1 rounded-xl px-1 py-1.5 transition-colors",
                      isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"
                    )
                  }>
                  {({ isActive }) => (
                    <>
                      <span className={cn("flex h-7 w-9 items-center justify-center rounded-lg transition-colors", isActive && "bg-primary/10")}>
                        <Icon className="h-5 w-5" />
                      </span>
                      <span className="max-w-full truncate text-[11px] font-medium leading-tight">{t(labelKey)}</span>
                    </>
                  )}
                </NavLink>
              )
            )}
          </div>
        </nav>
      )}
    </div>
  );
}
